import { useState, useEffect } from 'react'
import { supabase } from '../../../../shared/lib/supabase'
import { getIndustry, getSubCategory } from '../../../../shared/data/industries'
import {
  formatStaffingSize, formatItTeamSize, formatGeographicalPresence,
  getRiskAppetiteDisplay, calculateRiskAppetiteScore
} from '../../../../shared/utils/clientHelpers'

function ClientIdentitySidebar({ client, onEdit }) {
  const riskScore = calculateRiskAppetiteScore(client)
  const riskDisplay = getRiskAppetiteDisplay(riskScore)
  const [fieldProvenance, setFieldProvenance] = useState({})

  useEffect(() => {
    if (!client?.id) return
    supabase
      .from('client_intake_submissions')
      .select('field_provenance')
      .eq('client_id', client.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.field_provenance) setFieldProvenance(data.field_provenance)
      })
  }, [client?.id])

  const hasProv = (key) => fieldProvenance[key]?.source === 'client_intake'
  const provBorder = (key) => hasProv(key) ? { borderLeft: `3px solid ${fieldProvenance[key].modified_by_admin ? '#D97706' : '#0891B2'}`, paddingLeft: '8px' } : undefined

  return (
    <div className="space-y-3">
      {/* Card 1 — Primary Identity */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Navy gradient header */}
        <div
          className="px-5 py-4 relative"
          style={{ background: 'linear-gradient(135deg, #012438 0%, #213C4E 100%)' }}
        >
          {/* Edit button */}
          {onEdit && (
            <button
              onClick={onEdit}
              className="absolute top-3 right-3 p-1.5 rounded-md text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
              title="Edit Client"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          )}

          {/* Logo */}
          <div className="mb-3">
            {client.logoUrl ? (
              <img src={client.logoUrl} alt="" className="w-11 h-11 rounded-lg object-cover" />
            ) : (
              <div className="w-11 h-11 rounded-lg flex items-center justify-center text-sm font-bold text-white" style={{ backgroundColor: client.logoColor || 'rgba(138,208,214,0.15)', color: client.logoColor ? '#FFFFFF' : '#8AD0D6' }}>
                {client.logoInitials || (client.name || 'C').charAt(0).toUpperCase()}
              </div>
            )}
          </div>

          {/* Name + date */}
          <h2 className="text-lg font-bold text-white">{client.name}</h2>
          <p className="text-xs" style={{ color: '#BCC5CD' }}>
            Since {new Date(client.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
          </p>

          {/* Engagement status — bottom right */}
          <div className="flex justify-end mt-2">
            <div className={`px-2 py-1 rounded inline-flex items-center gap-1.5 ${
              client.status === 'retainer' ? 'bg-emerald-500/20' :
              client.status === 'suspended' ? 'bg-gray-500/20' :
              'bg-blue-500/20'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${
                client.status === 'retainer' ? 'bg-emerald-400' :
                client.status === 'suspended' ? 'bg-gray-400' :
                'bg-blue-400'
              }`} />
              <span className={`text-[11px] font-medium ${
                client.status === 'retainer' ? 'text-emerald-300' :
                client.status === 'suspended' ? 'text-gray-400' :
                'text-blue-300'
              }`}>
                {client.status === 'retainer' ? 'Retainer' : client.status === 'suspended' ? 'Suspended' : 'Discovery'}
              </span>
            </div>
          </div>
        </div>

        {/* Risk Appetite */}
        <div className={`mx-4 mt-4 mb-3 rounded-lg p-3 ${riskDisplay ? riskDisplay.bgClass : 'bg-gray-50 dark:bg-gray-700/30'}`}>
          {riskDisplay ? (
            <div className="flex items-center gap-3">
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: `linear-gradient(135deg, ${riskDisplay.gradientFrom}, ${riskDisplay.gradientTo})` }}
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={riskDisplay.iconPath} />
                </svg>
              </div>
              <div>
                <p className={`text-sm font-bold ${riskDisplay.textColor}`}>{riskDisplay.label}</p>
                <p className={`text-xs ${riskDisplay.subtextColor}`}>{riskDisplay.subtitle}</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-gray-200 dark:bg-gray-600">
                <svg className="w-6 h-6 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-bold text-gray-400 dark:text-gray-500">Not Assessed</p>
                <p className="text-xs text-gray-400 dark:text-gray-500">Complete risk appetite step</p>
              </div>
            </div>
          )}
        </div>

        {/* Industry */}
        <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700" style={provBorder('industry')}>
          <div className="flex items-center gap-1.5 mb-1">
            <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Industry</span>
          </div>
          <p className="text-xs font-bold text-gray-900 dark:text-white">
            {getIndustry(client.industry)?.label || client.industry || 'Not specified'}
          </p>
          {client.industrySubCategory && (
            <span className="inline-flex mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-accent-200">
              {getSubCategory(client.industry, client.industrySubCategory)?.label || client.industrySubCategory}
            </span>
          )}
        </div>

        {/* Org Stats 2x2 */}
        <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700">
          <div className="grid grid-cols-2 gap-3">
            <div style={provBorder('staffing_size')}>
              <div className="flex items-center gap-1 mb-0.5">
                <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Employees</span>
              </div>
              <p className="text-xs font-bold text-gray-900 dark:text-white">{formatStaffingSize(client.staffingSize)}</p>
            </div>
            <div style={provBorder('annual_revenue')}>
              <div className="flex items-center gap-1 mb-0.5">
                <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Revenue</span>
              </div>
              <p className="text-xs font-bold text-gray-900 dark:text-white">{client.annualRevenue || 'N/A'}</p>
            </div>
            <div style={provBorder('it_team_size')}>
              <div className="flex items-center gap-1 mb-0.5">
                <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">IT Team</span>
              </div>
              <p className="text-xs font-bold text-gray-900 dark:text-white">{formatItTeamSize(client.itTeamSize)}</p>
            </div>
            <div style={provBorder('geographical_presence')}>
              <div className="flex items-center gap-1 mb-0.5">
                <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Geography</span>
              </div>
              <p className="text-xs font-bold text-gray-900 dark:text-white">{formatGeographicalPresence(client.geographicalPresence)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Card 2 — Contacts */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center gap-1.5 mb-3">
          <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Contacts</span>
        </div>

        {/* Primary */}
        <div>
          {client.contactName ? (
            <p className="text-xs font-bold text-gray-900 dark:text-white">{client.contactName}</p>
          ) : (
            <p className="text-sm text-gray-400 dark:text-gray-500">No primary contact</p>
          )}
          {client.contactEmail && (
            <a href={`mailto:${client.contactEmail}`} className="text-xs text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-accent-300">
              {client.contactEmail}
            </a>
          )}
        </div>

        {/* Secondary */}
        {(client.secondaryContactName || client.secondaryContactEmail) && (
          <>
            <div className="border-t border-gray-100 dark:border-gray-700 my-2.5" />
            <div>
              {client.secondaryContactName && (
                <p className="text-xs font-bold text-gray-900 dark:text-white">{client.secondaryContactName}</p>
              )}
              {client.secondaryContactEmail && (
                <a href={`mailto:${client.secondaryContactEmail}`} className="text-xs text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-accent-300">
                  {client.secondaryContactEmail}
                </a>
              )}
            </div>
          </>
        )}
      </div>

      {/* Card 3 — Notes */}
      {client.notes && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-1.5 mb-2">
            <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Notes</span>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{client.notes}</p>
        </div>
      )}
    </div>
  )
}

export default ClientIdentitySidebar
